import { useParams } from 'react-router-dom'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getProcessItems } from '../../services'
import {
    Package,
    Cpu,
    PackagePlus,
    ChevronDown,
    ChevronRight,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import React, { useState, useMemo } from 'react'
import ProcessItemsTable from './ProcessItemsTable'
import { ImportItemMetadata } from '../../types/response'

const ProcessItems = () => {
    const { id } = useParams<{ id: string }>()
    const [expandedEquipmentCategories, setExpandedEquipmentCategories] =
        useState<Set<string>>(new Set())
    const [expandedEquipmentNames, setExpandedEquipmentNames] = useState<
        Set<string>
    >(new Set())
    const [expandedComponentCategories, setExpandedComponentCategories] =
        useState<Set<string>>(new Set())
    const [expandedComponentNames, setExpandedComponentNames] = useState<
        Set<string>
    >(new Set())
    const [expandedImportItems, setExpandedImportItems] = useState<Set<number>>(
        new Set()
    )

    const { data: items, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_PROCESSES, id, 'items'],
        queryFn: () => getProcessItems(id!),
    })

    const equipment = items?.equipment || []
    const components = items?.components || []
    const importItems: ImportItemMetadata[] = items?.import_items || []

    // Group equipment by category, then by name
    const groupedEquipment = useMemo(() => {
        const groups: Record<string, Record<string, any[]>> = {}
        equipment.forEach((item: any) => {
            const category = item.item?.category || 'uncategorized'
            const name = item.item?.name || 'Unknown'
            if (!groups[category]) {
                groups[category] = {}
            }
            if (!groups[category][name]) {
                groups[category][name] = []
            }
            groups[category][name].push(item)
        })
        return groups
    }, [equipment])

    // Group components by category, then by name
    const groupedComponents = useMemo(() => {
        const groups: Record<string, Record<string, any[]>> = {}
        components.forEach((component: any) => {
            const category = component.item?.category || 'uncategorized'
            const name = component.item?.name || 'Unknown'
            if (!groups[category]) {
                groups[category] = {}
            }
            if (!groups[category][name]) {
                groups[category][name] = []
            }
            groups[category][name].push(component)
        })
        return groups
    }, [components])

    // Toggle equipment category expansion
    const toggleEquipmentCategory = (category: string) => {
        setExpandedEquipmentCategories((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(category)) {
                newSet.delete(category)
            } else {
                newSet.add(category)
            }
            return newSet
        })
    }

    // Toggle equipment name expansion
    const toggleEquipmentName = (key: string) => {
        setExpandedEquipmentNames((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(key)) {
                newSet.delete(key)
            } else {
                newSet.add(key)
            }
            return newSet
        })
    }

    // Toggle component category expansion
    const toggleComponentCategory = (category: string) => {
        setExpandedComponentCategories((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(category)) {
                newSet.delete(category)
            } else {
                newSet.add(category)
            }
            return newSet
        })
    }

    // Toggle component name expansion
    const toggleComponentName = (key: string) => {
        setExpandedComponentNames((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(key)) {
                newSet.delete(key)
            } else {
                newSet.add(key)
            }
            return newSet
        })
    }

    // Toggle import item expansion
    const toggleImportItem = (index: number) => {
        setExpandedImportItems((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(index)) {
                newSet.delete(index)
            } else {
                newSet.add(index)
            }
            return newSet
        })
    }

    // Format date for display
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '-'
        return new Date(dateStr).toLocaleDateString('vi-VN')
    }

    // Format currency
    const formatCurrency = (amount?: number) => {
        if (!amount) return '-'
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        }).format(amount)
    }

    // Generate preview ref following backend legacy pattern
    const generateRefPreview = (
        category: string,
        name: string,
        purchaseDate?: string,
        parentRef?: string
    ): string => {
        const cat = category.toUpperCase()
        const cleanName = name
            .toUpperCase()
            .replace(/[^A-Z0-9\s]/g, '')
            .trim()
            .slice(0, 20)
        const date = purchaseDate ? new Date(purchaseDate) : new Date()
        const yyyymm = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`
        const ref = cleanName
            ? `${cat}.${cleanName}.${yyyymm}`
            : `${cat}.${yyyymm}`
        return parentRef ? `${parentRef}_${ref}` : ref
    }

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    const hasItems =
        equipment.length > 0 || components.length > 0 || importItems.length > 0

    if (!hasItems) {
        return (
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle className="text-sm">Process Items</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-muted-foreground">
                        No items associated with this process
                    </p>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className="mt-2">
            <CardHeader>
                <CardTitle className="text-sm">Process Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Equipment Section */}
                {equipment.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Package className="w-4 h-4 text-blue-600" />
                            <h3 className="text-sm font-semibold text-gray-900">
                                Equipment
                            </h3>
                            <Badge variant="outline" className="ml-auto">
                                {equipment.length}
                            </Badge>
                        </div>
                        <ProcessItemsTable
                            groupedItems={groupedEquipment}
                            expandedCategories={expandedEquipmentCategories}
                            expandedNames={expandedEquipmentNames}
                            toggleCategory={toggleEquipmentCategory}
                            toggleName={toggleEquipmentName}
                            itemType="equipment"
                        />
                    </div>
                )}

                {/* Components Section */}
                {components.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Cpu className="w-4 h-4 text-purple-600" />
                            <h3 className="text-sm font-semibold text-gray-900">
                                Components
                            </h3>
                            <Badge variant="outline" className="ml-auto">
                                {components.length}
                            </Badge>
                        </div>
                        <ProcessItemsTable
                            groupedItems={groupedComponents}
                            expandedCategories={expandedComponentCategories}
                            expandedNames={expandedComponentNames}
                            toggleCategory={toggleComponentCategory}
                            toggleName={toggleComponentName}
                            itemType="component"
                        />
                    </div>
                )}

                {/* Import Items Section (pending creation) */}
                {importItems.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <PackagePlus className="w-4 h-4 text-green-600" />
                            <h3 className="text-sm font-semibold text-gray-900">
                                Items to Import
                            </h3>
                            <Badge variant="secondary" className="ml-auto">
                                {importItems.length} pending
                            </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mb-2">
                            These items will be created in the system when this
                            process is approved. Click a row to view details.
                        </div>
                        <div className="border rounded-md overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/50">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-medium w-8"></th>
                                        <th className="px-3 py-2 text-left font-medium">
                                            Name
                                        </th>
                                        <th className="px-3 py-2 text-left font-medium">
                                            Category
                                        </th>
                                        <th className="px-3 py-2 text-left font-medium">
                                            Type
                                        </th>
                                        <th className="px-3 py-2 text-left font-medium">
                                            Qty/Tags
                                        </th>
                                        <th className="px-3 py-2 text-left font-medium">
                                            Condition
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {importItems.map((item, index) => {
                                        const isExpanded =
                                            expandedImportItems.has(index)
                                        return (
                                            <React.Fragment key={index}>
                                                <tr
                                                    className="border-t cursor-pointer hover:bg-muted/30"
                                                    onClick={() =>
                                                        toggleImportItem(index)
                                                    }
                                                >
                                                    <td className="px-3 py-2">
                                                        {isExpanded ? (
                                                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 font-medium">
                                                        {item.name}
                                                    </td>
                                                    <td className="px-3 py-2 capitalize">
                                                        {item.category}
                                                    </td>
                                                    <td className="px-3 py-2 capitalize">
                                                        {item.item_type}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {item.service_tags
                                                            ?.length > 0 ? (
                                                            <span>
                                                                {
                                                                    item
                                                                        .service_tags
                                                                        .length
                                                                }{' '}
                                                                tags
                                                            </span>
                                                        ) : (
                                                            <div className="flex items-center gap-1">
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="text-xs font-mono"
                                                                >
                                                                    {generateRefPreview(
                                                                        item.category,
                                                                        item.name,
                                                                        item.purchase_date
                                                                    )}
                                                                </Badge>
                                                                {(item.quantity ||
                                                                    1) > 1 && (
                                                                    <Badge
                                                                        variant="outline"
                                                                        className="text-xs"
                                                                    >
                                                                        ×
                                                                        {
                                                                            item.quantity
                                                                        }
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 capitalize">
                                                        {item.condition}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="bg-muted/20">
                                                        <td
                                                            colSpan={6}
                                                            className="px-6 py-4"
                                                        >
                                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                                <div>
                                                                    <div className="text-muted-foreground text-xs">
                                                                        Owner
                                                                    </div>
                                                                    <div>
                                                                        {item.owner ||
                                                                            '-'}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-muted-foreground text-xs">
                                                                        Status
                                                                    </div>
                                                                    <div className="capitalize">
                                                                        {item.status ||
                                                                            '-'}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-muted-foreground text-xs">
                                                                        Purchase
                                                                        Price
                                                                    </div>
                                                                    <div>
                                                                        {formatCurrency(
                                                                            item.purchase_price
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-muted-foreground text-xs">
                                                                        Purchase
                                                                        Date
                                                                    </div>
                                                                    <div>
                                                                        {formatDate(
                                                                            item.purchase_date
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-muted-foreground text-xs">
                                                                        Warranty
                                                                        Start
                                                                    </div>
                                                                    <div>
                                                                        {formatDate(
                                                                            item.warranty_start
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-muted-foreground text-xs">
                                                                        Warranty
                                                                        End
                                                                    </div>
                                                                    <div>
                                                                        {formatDate(
                                                                            item.warranty_end
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {item.description && (
                                                                    <div className="col-span-2">
                                                                        <div className="text-muted-foreground text-xs">
                                                                            Description
                                                                        </div>
                                                                        <div>
                                                                            {
                                                                                item.description
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {item.service_tags &&
                                                                    item
                                                                        .service_tags
                                                                        .length >
                                                                        0 && (
                                                                        <div className="col-span-full">
                                                                            <div className="text-muted-foreground text-xs mb-1">
                                                                                Service
                                                                                Tags
                                                                                /
                                                                                Serial
                                                                                Numbers
                                                                            </div>
                                                                            <div className="flex flex-wrap gap-1">
                                                                                {item.service_tags.map(
                                                                                    (
                                                                                        tag,
                                                                                        tagIndex
                                                                                    ) => (
                                                                                        <Badge
                                                                                            key={
                                                                                                tagIndex
                                                                                            }
                                                                                            variant="outline"
                                                                                            className="text-xs"
                                                                                        >
                                                                                            {
                                                                                                tag
                                                                                            }
                                                                                        </Badge>
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                {/* Children Components */}
                                                                {item.children &&
                                                                    item
                                                                        .children
                                                                        .length >
                                                                        0 && (
                                                                        <div className="col-span-full">
                                                                            <div className="text-muted-foreground text-xs">
                                                                                Child
                                                                                Components
                                                                            </div>
                                                                            <div className="flex flex-wrap gap-1">
                                                                                {item.children.map(
                                                                                    (
                                                                                        child,
                                                                                        childIndex
                                                                                    ) =>
                                                                                        child
                                                                                            .service_tags
                                                                                            ?.length >
                                                                                        0 ? (
                                                                                            child.service_tags.map(
                                                                                                (
                                                                                                    tag,
                                                                                                    i
                                                                                                ) => (
                                                                                                    <Badge
                                                                                                        key={`${childIndex}-${i}`}
                                                                                                        variant="outline"
                                                                                                        className="text-xs"
                                                                                                    >
                                                                                                        {
                                                                                                            child.name
                                                                                                        }

                                                                                                        :
                                                                                                        {
                                                                                                            tag
                                                                                                        }
                                                                                                    </Badge>
                                                                                                )
                                                                                            )
                                                                                        ) : (
                                                                                            <Badge
                                                                                                key={
                                                                                                    childIndex
                                                                                                }
                                                                                                variant="secondary"
                                                                                                className="text-xs font-mono"
                                                                                            >
                                                                                                {
                                                                                                    child.name
                                                                                                }

                                                                                                :{' '}
                                                                                                {generateRefPreview(
                                                                                                    child.category,
                                                                                                    child.name,
                                                                                                    child.purchase_date,
                                                                                                    item
                                                                                                        .service_tags?.[0] ||
                                                                                                        generateRefPreview(
                                                                                                            item.category,
                                                                                                            item.name,
                                                                                                            item.purchase_date
                                                                                                        )
                                                                                                )}
                                                                                                {(child.quantity ||
                                                                                                    1) >
                                                                                                    1 &&
                                                                                                    ` ×${child.quantity}`}
                                                                                            </Badge>
                                                                                        )
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export default ProcessItems
